import { toast } from 'react-toastify'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Eye, Pencil, Trash } from 'phosphor-react'

import styles from './styles.module.scss'

import api from '../../../../../services/api'

import { Aluno } from '../../../../../domain/entities/aluno.entity'
import { useAuth } from '../../../../../hooks/useAuth'
import { useListStudents } from '../@hooks/use-list-students'

import Tooltip from '../../../../../components/Tooltip'
import { Modal } from '../../../../../components/Modal'
import { Loader } from '../../../../../components/Loader'
import { Pagination } from '../../../../../components/Pagination'
import { SelectPerPage } from '../../../../../components/Pagination/Select'

export default function AlunosPage() {
  const { token } = useAuth()
  const navigate = useNavigate()
  const [perPage, setPerPage] = useState(20)
  const [loading, setLoading] = useState(false)
  const [searchName, setSearchName] = useState('')
  const [debouncedValue, setDebouncedValue] = useState(searchName)
  const [startSearch, setStartSearch] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [searchFilter, setSearchFilter] = useState('alunonome')
  const [deleteStudent, setDeleteStudent] = useState({} as Aluno)

  const { isPending, count, students, error } = useListStudents({
    page: currentPage,
    perPage,
    property: startSearch && searchFilter,
    value: startSearch && debouncedValue,
  })

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(searchName), 500)
    return () => clearTimeout(handler)
  }, [searchName])

  const findByProperty = () => setStartSearch(true)

  function handleDetailsStudent(id: number) {
    navigate(`/aluno/${id}`)
  }

  function handleEditStudent(student: Aluno) {
    navigate(`edit/${student.id}`, { state: student })
  }

  const handleDeleteStudent = (student: Aluno) => {
    setDeleteStudent(student)
  }

  const handleCloseModal = () => setDeleteStudent({} as Aluno)

  const handleConfirmDeleteStudent = async () => {
    try {
      setLoading(true)
      const headers = { Authorization: `Bearer ${token}` }
      const { data } = await api.get(
        `/admin/students/documents/${deleteStudent.id}`,
        { headers },
      )

      const promises = data.documents.map((doc) => {
        return api.delete(`/admin/students/documents/delete/${doc.id}`, {
          headers,
        })
      })
      await Promise.all(promises)

      await api.delete(`/admin/students/${deleteStudent.id}`, { headers })

      setDeleteStudent({} as Aluno)
      toast('Aluno deletado com sucesso.')
    } catch (error) {
      toast.error('Ocorreu algum erro ao tentar deletar o aluno.')
      setDeleteStudent({} as Aluno)
      console.error('error: ', error)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className={styles.main_container}>
      <h1>Administrador - Alunos</h1>
      <div className={styles.search}>
        <input
          value={searchName}
          onChange={(e) => setSearchName(e.target.value)}
        />
        <select
          value={searchFilter}
          onChange={(e) => setSearchFilter(e.target.value)}
        >
          <option value={'alunonome'}>Nome do Aluno</option>
          <option value={'alunocpf'}>CPF do Aluno</option>
          <option value={'responsavelnome'}>Nome do Responsavel</option>
          <option value={'responsavelcpf'}>CPF do Responsavel</option>
          <option value={'email'}>Email</option>
        </select>
        <button onClick={findByProperty}>Search</button>
        <SelectPerPage perPage={perPage} setPerPage={setPerPage} />
      </div>

      {(isPending || loading) && (
        <>
          <Loader /> <h1 className={styles.loading}>...Buscando</h1>
        </>
      )}

      {deleteStudent?.id && (
        <div className={styles.content_modal}>
          <Modal.Root content="" title="Excluir Aluno">
            <span>
              Deseja realmente excluir o aluno {deleteStudent.alunonome}?<br />
              Essa ação não pode ser desfeita
            </span>
            <Modal.ButtonRoot>
              <Modal.Button labelText="Cancelar" onClick={handleCloseModal} />
              <Modal.Button
                labelText="Excluir"
                onClick={handleConfirmDeleteStudent}
                isPrimary
              />
            </Modal.ButtonRoot>
          </Modal.Root>
        </div>
      )}

      {!isPending && (
        <table className={styles.table_container}>
          <thead>
            <tr>
              <td>Identificador</td>
              <td>Nome</td>
              <td>Cpf</td>
              <td>Responsável</td>
              <td>CPF Responsável</td>
              <td>Email</td>
              <div className={styles.actions}>Ações</div>
            </tr>
          </thead>

          {students?.length > 0 &&
            students.map((student) => {
              return (
                <tr>
                  <td>{student.id}</td>
                  <td>{student.alunonome}</td>
                  <td>{student.alunocpf}</td>
                  <td>{student.responsavelnome}</td>
                  <td>{student.responsavelcpf}</td>
                  <td>{student.responsavelemail}</td>
                  <div className={styles.actions}>
                    <Tooltip title="Detalhes">
                      <Eye
                        onClick={() => handleDetailsStudent(student.id)}
                        size={24}
                      />
                    </Tooltip>

                    <Tooltip title="Editar">
                      <Pencil
                        onClick={() => handleEditStudent(student)}
                        size={24}
                      />
                    </Tooltip>

                    <Tooltip title="Excluir">
                      <Trash
                        onClick={() => handleDeleteStudent(student)}
                        size={24}
                      />
                    </Tooltip>
                  </div>
                </tr>
              )
            })}

          {students?.length === 0 && <p>Nenhum aluno encontrado</p>}
        </table>
      )}

      <Pagination
        count={count}
        perPage={perPage}
        page={currentPage}
        setCurrentPage={setCurrentPage}
      />
    </div>
  )
}
